import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ResponseWrapper } from "@app/integrations/urm/models/response-wrapper";
import { PropTenantAttribute } from "@app/integrations/urm/models/prop-tenant-attribute";
import { PropTenantAttributeCode } from "@app/integrations/urm/models/prop-tenant-attribute-code";
import { PropPropertyAttribute } from "@app/integrations/urm/models/prop-property-attribute";
import { PropPropertyAttributeCode } from "@app/integrations/urm/models/prop-property-attribute-code";
import { TextAnonymisationBehaviour } from "@app/integrations/urm/models/prop-tenant-attribute-code";
import { ClientConfigService } from "@app/core/config/client-config.service";

@Injectable({
  providedIn: "root"
})
export class CautionService {
  private clientConfigService = inject(ClientConfigService);
  private http = inject(HttpClient);

  private static PATHS = {
    GetTenantAttributeByPropertyID:
      "/Services/UniclassRIA-Web-Services-Property-Cautions.svc/JSON/GetTenantAttributeByPropertyID",
    PropertyHasCautions:
      "/Services/UniclassRIA-Web-Services-Property-Cautions.svc/Json/PropertyHasCautions",
    TenantAttributeCodeLookup:
      "/Services/UniclassRIA-Web-Services-Property-Cautions.svc/Json/GetTenantAttributeCodeByID",
    GetCautionsByPropertyID:
      "/Services/UniclassRIA-Web-Services-Property-Cautions.svc/JSON/GetPropertyAttributeCautionsByID",
    PropertyAttributeCodeLookup:
      "/Services/UniclassRIA-Web-Services-Property-AttributeCodes.svc/Json/GetAttributeCodesByID"
  };

  private get simulate(): boolean {
    return !!this.clientConfigService.config.urmServices.simulate;
  }

  loadTenantAttributeByPropertyID(id: number): Promise<PropTenantAttribute[]> {
    if (this.simulate) {
      const attr = new PropTenantAttribute();
      attr.Ptat_TenantAttributeID = 1;
      attr.Ptat_TenantID = "T-" + id;
      attr.Ptat_TenantAttributeCodeID = "DOG";
      attr.Ptat_Notes = "Tenant has a large dog — approach with caution.";
      return Promise.resolve([attr]);
    }
    return new Promise<PropTenantAttribute[]>((resolve, reject) => {
      const options = { params: { propertyId: id.toString(10) } };
      this.http
        .get(
          this.clientConfigService.config.urmServices.serviceUrl +
            CautionService.PATHS.GetTenantAttributeByPropertyID,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetTenantAttributeByPropertyIDResult as ResponseWrapper<PropTenantAttribute>;
            if (!resultWrapper.RootResults) {
              reject("Error retrieving tenant attributes.");
            }
            resolve(resultWrapper.RootResults);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  loadPropertyAttributeCautionsByPropertyID(
    id: number
  ): Promise<PropPropertyAttribute[]> {
    if (this.simulate) {
      const attr = new PropPropertyAttribute();
      attr.Attb_AttributeID = 1;
      attr.Attb_PropertyIdentity = id;
      attr.Attb_AttributeCodeID = "ASBESTOS";
      return Promise.resolve([attr]);
    }
    return new Promise<PropPropertyAttribute[]>((resolve, reject) => {
      const options = { params: { propertyId: id.toString(10) } };
      this.http
        .get(
          this.clientConfigService.config.urmServices.serviceUrl +
            CautionService.PATHS.GetCautionsByPropertyID,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetPropertyAttributeCautionsByIDResult as ResponseWrapper<PropPropertyAttribute>;
            if (!resultWrapper.RootResults) {
              reject("Error retrieving property attributes.");
            }
            resolve(resultWrapper.RootResults);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  hasCautions(id: string): Promise<boolean> {
    if (this.simulate) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve, reject) => {
      const options = { params: { propertyId: id } };
      this.http
        .get(
          this.clientConfigService.config.urmServices.serviceUrl +
            CautionService.PATHS.PropertyHasCautions,
          options
        )
        .subscribe(
          (value: any) => {
            let resultBoolean = value.PropertyHasCautionsResult as boolean;
            console.log("cautions: " + resultBoolean);
            resolve(resultBoolean);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  tenantAttributeCodeLookup(id: string): Promise<PropTenantAttributeCode> {
    if (this.simulate) {
      const code = new PropTenantAttributeCode();
      code.Ptac_TenantAttributeCodeID = id;
      code.Ptac_Description =
        id === "DOG" ? "Dog on premises" : "Tenant caution: " + id;
      code.Ptac_Caution = true;
      code.Ptac_TextAnonymisationBehaviour =
        TextAnonymisationBehaviour.NotAnonymised;
      code.Ptac_AlternateText = "";
      return Promise.resolve(code);
    }
    return new Promise<PropTenantAttributeCode>((resolve, reject) => {
      const options = { params: { codeId: id } };
      this.http
        .get(
          this.clientConfigService.config.urmServices.serviceUrl +
            CautionService.PATHS.TenantAttributeCodeLookup,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetTenantAttributeCodeByIDResult as ResponseWrapper<PropTenantAttributeCode>;
            if (!resultWrapper.RootResults) {
              reject("Error retrieving tenant attributes.");
            }
            resolve(resultWrapper.RootResults[0]);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  propertyAttributeCodeLookup(id: string): Promise<PropPropertyAttributeCode> {
    if (this.simulate) {
      const code = new PropPropertyAttributeCode();
      code.Attc_AttributeCodeID = id;
      code.Attc_Description =
        id === "ASBESTOS"
          ? "Asbestos present — PPE required"
          : "Property caution: " + id;
      code.Attc_AttributeTypeID = "CAUTION";
      code.Attc_Caution = true;
      code.Attc_TextAnonymisationBehaviour =
        TextAnonymisationBehaviour.NotAnonymised;
      code.Attc_AlternateText = "";
      return Promise.resolve(code);
    }
    return new Promise<PropPropertyAttributeCode>((resolve, reject) => {
      const options = { params: { codeId: id } };
      this.http
        .get(
          this.clientConfigService.config.urmServices.serviceUrl +
            CautionService.PATHS.PropertyAttributeCodeLookup,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetAttributeCodesByIDResult as ResponseWrapper<PropPropertyAttributeCode>;
            if (!resultWrapper.RootResults) {
              reject("Error retrieving property attribute code lookup.");
            }
            resolve(resultWrapper.RootResults[0]);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }
}
